import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mountain, Diamond, LineChart, FolderOpen, Scale, CreditCard,
  Landmark, Briefcase, Compass, ArrowRight,
} from 'lucide-react';
import PortalShell from '@/components/portal/PortalShell';
import PublicHero from '@/components/portal/PublicHero';
import { EmptyState } from '@/components/eds';
import { contentService, type ServiceItem } from '@/services/contentService';

// WP2.1B-R2: rebuilt to the LOCKED Services composition (giisdecsite_30 · services).
// Presentation-only. The service catalogue is still driven by
// contentService.listServices() (geo-api → governed published fallback); no
// service content is hard-coded. The card visual template, the "service for
// every role" panel and the governed status board are the locked composition.

// Presentation-only rotation of locked card icons + action affordances.
const CARD_ICONS = [Mountain, Diamond, LineChart, FolderOpen, Scale, CreditCard];
const CARD_ACTIONS = ['Open Map', 'Browse Data', 'Get Access', 'View Catalogue', 'Start Request', 'See Plans'];

const ROLES = [
  { icon: <Landmark className="w-5 h-5" />, title: 'Government Agencies', body: 'Executive oversight, regulatory governance and inter-agency reporting.' },
  { icon: <Briefcase className="w-5 h-5" />, title: 'Investors', body: 'Discover, evaluate and act on verified mining opportunities.' },
  { icon: <Compass className="w-5 h-5" />, title: 'Explorers & Partners', body: 'Access geological intelligence and spatial datasets for the field.' },
];

// Capability readiness board. P9B: qualitative pilot states only — NO measured
// percentages, version numbers or live/uptime claims are asserted (none are
// sourced). Progress-bar figures and fabricated version/"Live" labels removed.
const STATUS = [
  { title: 'Public Dataset Catalogue', state: 'Limited availability' },
  { title: 'Concession GIS Layers', state: 'Under validation' },
  { title: 'National Mineral Index', state: 'Under validation' },
  { title: 'Investor Onboarding', state: 'Limited availability' },
];

const Services: React.FC = () => {
  const [items, setItems] = useState<ServiceItem[]>([]);
  useEffect(() => { contentService.listServices().then(setItems); }, []);

  const services = Array.isArray(items) ? items : [];

  return (
    <PortalShell>
      <PublicHero
        badge="Service Categories"
        title={<>Everything you need,<br /><span className="text-eds-mint">in one portal</span></>}
        subtitle="Select the pathway that matches your needs across Ghana's national geological infrastructure."
      />

      {/* Service catalogue — data-driven cards in the locked card template */}
      <section className="py-16 md:py-20 bg-eds-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {services.length === 0 ? (
            <EmptyState
              title="Services are being published"
              message="Approved service categories will appear here once released by GIISDEC."
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s, i) => {
                const Icon = CARD_ICONS[i % CARD_ICONS.length];
                const action = CARD_ACTIONS[i % CARD_ACTIONS.length];
                return (
                  <div
                    key={s.id}
                    className="group bg-eds-surface border border-eds-border rounded-eds-lg p-7 shadow-eds-sm hover:shadow-eds-md hover:-translate-y-0.5 transition"
                  >
                    <div className="w-12 h-12 rounded-eds-md bg-eds-success/10 border border-eds-success/20 flex items-center justify-center text-eds-emerald-2">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex items-center gap-2 mt-5">
                      <h3 className="font-eds-display font-bold text-eds-fg">{s.title}</h3>
                    </div>
                    <span className="inline-block mt-2 text-[10px] font-semibold uppercase tracking-widest text-eds-earth bg-eds-gold/12 border border-eds-gold/25 rounded-eds-pill px-1.5 py-0.5 font-eds-mono">
                      {s.publishStatus}
                    </span>
                    <p className="text-sm text-eds-muted-fg mt-3 leading-relaxed">{s.description}</p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-eds-emerald-2 hover:underline"
                    >
                      {action} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* A service for every role */}
      <section className="py-16 md:py-20 bg-eds-surface-2/40 border-y border-eds-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-eds-emerald-2">Built for every stakeholder</p>
            <h2 className="font-eds-display text-3xl md:text-4xl font-bold text-eds-fg mt-3 tracking-[-0.01em]">
              A service for every role
            </h2>
            <ul className="mt-8 divide-y divide-eds-border">
              {ROLES.map((r) => (
                <li key={r.title} className="flex items-start gap-4 py-5">
                  <span className="w-10 h-10 flex-shrink-0 rounded-eds-md bg-eds-surface border border-eds-border flex items-center justify-center text-eds-emerald-2">
                    {r.icon}
                  </span>
                  <div>
                    <h3 className="font-semibold text-eds-fg">{r.title}</h3>
                    <p className="text-sm text-eds-muted-fg mt-0.5">{r.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-eds-lg bg-eds-forest text-white p-6 md:p-8 shadow-eds-md space-y-4">
            {STATUS.map((s) => (
              <div key={s.title} className="rounded-eds-md bg-white/5 border border-white/10 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-semibold text-white/90 text-sm">{s.title}</div>
                  {/* Qualitative readiness pill — no measured percentage is implied. */}
                  <span className="inline-flex items-center rounded-eds-pill border border-white/20 bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-eds-mint">
                    {s.state}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PortalShell>
  );
};

export default Services;
